import type { RateImportSession, RateReplyPayload, RateStreamCallbacks, RateStreamStatus } from "./rates.types";

import { resolveBackofficeOrigin } from "@qubictron/url/client";

const CSRF_COOKIE_URL = "/sanctum/csrf-cookie";

const csrfToken = (): string => {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);

  return match ? decodeURIComponent(match[1]) : "";
};

const ensureCsrf = async (origin: string): Promise<void> => {
  if (csrfToken()) return;

  await fetch(`${origin}${CSRF_COOKIE_URL}`, { credentials: "include" });
};

export const processSSEStream = async (response: Response, callbacks: RateStreamCallbacks): Promise<void> => {
  const reader = response.body?.getReader();

  if (!reader) {
    callbacks.onError?.("No response body");

    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let receivedComplete = false;
  let currentEvent = "";
  let currentData = "";

  const dispatch = (event: string, data: string) => {
    try {
      const parsed = JSON.parse(data) as {
        type?: string;
        status?: RateStreamStatus;
        content?: string;
        session?: RateImportSession;
        message?: string;
      };

      if (event === "status") callbacks.onStatus?.(parsed.status as RateStreamStatus);
      else if (event === "delta") callbacks.onDelta?.(parsed.content ?? "");
      else if (event === "complete") callbacks.onComplete?.(parsed.session as RateImportSession);
      else if (event === "error") callbacks.onError?.(parsed.message ?? "Stream error");
    } catch {
      if (event === "delta") callbacks.onDelta?.(data);
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (!done) buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line.startsWith("event:")) currentEvent = line.slice(6).trim();
        else if (line.startsWith("data:")) currentData = line.slice(5).trim();
        else if (line === "" && currentEvent && currentData) {
          if (currentEvent === "complete") receivedComplete = true;
          dispatch(currentEvent, currentData);
          currentEvent = "";
          currentData = "";
        }
      }

      if (!done) continue;

      if (currentEvent && currentData) {
        if (currentEvent === "complete") receivedComplete = true;
        dispatch(currentEvent, currentData);
      }

      if (!receivedComplete) {
        callbacks.onError?.(
          "Stream ended unexpectedly. The response may have been received, but the connection closed before completion."
        );
      }

      break;
    }
  } catch (error) {
    callbacks.onError?.(error instanceof Error ? error.message : "Stream error");
  } finally {
    reader.releaseLock();
  }
};

const postStream = async (
  path: string,
  init: { json?: unknown; form?: FormData },
  callbacks: RateStreamCallbacks
): Promise<void> => {
  const origin = resolveBackofficeOrigin();
  await ensureCsrf(origin);

  const headers: HeadersInit = {
    Accept: "text/event-stream",
    "X-XSRF-TOKEN": csrfToken(),
  };

  let body: BodyInit;

  if (init.form) {
    body = init.form;
  } else {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(init.json ?? {});
  }

  const send = () =>
    fetch(`${origin}${path}`, {
      method: "POST",
      headers,
      body,
      credentials: "include",
    });

  let response = await send();

  if (response.status === 419) {
    await fetch(`${origin}${CSRF_COOKIE_URL}`, { credentials: "include" });
    headers["X-XSRF-TOKEN"] = csrfToken();
    response = await send();
  }

  if (!response.ok) {
    callbacks.onError?.(`HTTP error: ${response.status}`);

    return;
  }

  await processSSEStream(response, callbacks);
};

export const streamRatesImport = async (
  message: string,
  files: File[],
  callbacks: RateStreamCallbacks
): Promise<void> => {
  if (files.length > 0) {
    const form = new FormData();
    if (message) form.append("message", message);
    if (files.length === 1) form.append("file", files[0]);
    else files.forEach((file) => form.append("files[]", file));

    await postStream("/api/rates/import-sessions", { form }, callbacks);

    return;
  }

  await postStream("/api/rates/import-sessions", { json: { message } }, callbacks);
};

export const streamRatesReply = async (
  sessionId: number,
  payload: RateReplyPayload,
  callbacks: RateStreamCallbacks
): Promise<void> => {
  const files = payload.files ?? [];

  if (files.length > 0) {
    const form = new FormData();
    if (payload.message) form.append("message", payload.message);
    if (payload.issue_code) form.append("issue_code", payload.issue_code);
    if (payload.action) form.append("action", payload.action);
    if (payload.custom_value) form.append("custom_value", payload.custom_value);
    if (files.length === 1) form.append("file", files[0]);
    else files.forEach((file) => form.append("files[]", file));

    await postStream(`/api/rates/import-sessions/${sessionId}/messages`, { form }, callbacks);

    return;
  }

  await postStream(
    `/api/rates/import-sessions/${sessionId}/messages`,
    {
      json: {
        message: payload.message,
        issue_code: payload.issue_code,
        action: payload.action,
        custom_value: payload.custom_value,
      },
    },
    callbacks
  );
};
